import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';

function PurchaseShardsReturn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser, loading: authLoading, isAuthenticated } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkSessionStatus = useCallback(async (sessionId) => {
    try {
      // First check session status
      const statusResponse = await fetch(`${API_URL}/stripe/session-status?session_id=${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setStatus(statusData.status);

        if (statusData.status === 'complete') {
          // Payment succeeded - try to fulfill purchase (add shards)
          // This is a fallback in case webhook didn't fire
          try {
            const fulfillResponse = await fetch(`${API_URL}/stripe/fulfill-purchase`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ session_id: sessionId })
            });

            if (fulfillResponse.ok) {
              const fulfillData = await fulfillResponse.json();
              // Refresh user data to get updated shards
              await refreshUser();
            } else {
              // Try to parse error, but don't fail if it's HTML
              try {
                const fulfillData = await fulfillResponse.json();
                console.error('Failed to fulfill purchase:', fulfillData);
              } catch (e) {
                console.error('Failed to fulfill purchase (non-JSON response)');
              }
              // Even if fulfillment fails, payment succeeded, so show success
              // User can contact support if shards weren't added
            }
          } catch (fulfillError) {
            console.error('Error fulfilling purchase:', fulfillError);
            // Continue anyway - payment succeeded
          }

          // Refresh user data
          await refreshUser();
        }
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error checking session status:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [token, refreshUser]);

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) {
      return;
    }

    // Redirect to sign in if not authenticated
    if (!isAuthenticated || !token) {
      navigate('/signin?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }

    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setLoading(false);
      return;
    }

    checkSessionStatus(sessionId);
  }, [searchParams, token, isAuthenticated, authLoading, navigate, checkSessionStatus]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center p-5">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white/60 mb-4"></div>
          <p className="text-white/60">Processing payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'complete') {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center p-5">
        <div className="max-w-md w-full">
          <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-10 shadow-2xl text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500/50 flex items-center justify-center">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-white/60 mb-8">
              Your shards have been added to your account.
            </p>

            <button
              onClick={() => navigate('/account')}
              className="w-full py-3.5 px-6 rounded-xl font-semibold transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 shadow-lg shadow-accent-blue/20"
            >
              View Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'open' || status === 'error') {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center p-5">
        <div className="max-w-md w-full">
          <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-10 shadow-2xl text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-full bg-red-500/20 border-2 border-red-500/50 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white mb-2">Payment Failed</h2>
            <p className="text-white/60 mb-8">
              {status === 'open' 
                ? 'The payment was not completed. Please try again.'
                : 'There was an error processing your payment. Please try again.'}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => navigate('/purchase-shards')}
                className="flex-1 py-3.5 px-6 rounded-xl font-semibold transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 py-3.5 px-6 rounded-xl font-semibold transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70"
              >
                Back to Store
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default PurchaseShardsReturn;

